import { POSTHOG } from "./config";
import type { PostHogSDK } from "./types";

declare global {
  interface Window {
    /** "off" | "full" once the SDK has applied the shared decision. */
    __posthogMode?: string;
    /** Whether session replay is running (explicit Accept only). */
    __posthogReplay?: boolean;
  }
}

/**
 * Applies the shared decision (lib/consent/privacy.ts) to PostHog
 * (measurement-consent C11/C13, zee 2026-11-04):
 *   - allowed (the regional default or an Accept) → "full": opt_in_capturing(),
 *     cookies and localStorage as usual, so returning visitors link across days
 *     and identify() works (cookieless PostHog can't hold an identity);
 *   - not allowed (GPC, decline, opt-in region, unknown) → "off":
 *     opt_out_capturing() drops PostHog's cookies, and `before_send` in the init
 *     config discards every event, so nothing is sent at all.
 * Session replay is separate: it starts only after an explicit Accept
 * (privacyConsent.storage) and stops otherwise; init disables it.
 *
 * Standalone so the exact same function runs inside the SDK loaded callback,
 * which passes the instance: at that moment `window.posthog` can still be the
 * snippet's stub. (A `window.posthog !== sdk` guard there returned early on every
 * load from 2026-10-29 until 2026-11-04, so the consent sync never ran, PostHog
 * stayed opted out, and $pageview stopped.)
 */
export function syncPostHogConsent(instance?: PostHogSDK) {
  const sdk: PostHogSDK | undefined = instance ?? window.posthog;
  if (!sdk || !window.__posthog?.loaded) return;
  const p = window.privacyConsent;
  const mode = p?.ready && p.allowed ? "full" : "off";
  const replay = mode === "full" && p?.storage === true;
  if (replay !== (window.__posthogReplay === true)) {
    window.__posthogReplay = replay;
    if (replay) sdk.startSessionRecording?.();
    else sdk.stopSessionRecording?.();
  }
  const previous = window.__posthogMode;
  if (mode === previous) return;
  window.__posthogMode = mode;
  // Default-based activation is not an explicit $opt_in conversion.
  if (mode === "full") sdk.opt_in_capturing?.({ captureEventName: false });
  else sdk.opt_out_capturing?.();
  // One explicit pageview when measurement starts.
  if (mode === "full") {
    sdk.capture?.("$pageview", { $current_url: window.location.href });
  }
}

export function getPostHogInitScript(): string {
  return `
    (function () {
      if (!window.privacyConsent?.allowed) return;
      !function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.async=!0,p.src=s.api_host.replace(".i.posthog.com","-assets.i.posthog.com")+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="init capture register register_once register_for_session unregister opt_out_capturing has_opted_out_capturing opt_in_capturing reset isFeatureEnabled getFeatureFlag getFeatureFlagPayload reloadFeatureFlags group identify setPersonProperties setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags resetGroups onFeatureFlags addFeatureFlagsHandler onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey getNextSurveyStep".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);
      posthog.init('${POSTHOG.API_KEY}', {
        api_host: '${POSTHOG.API_HOST}',
        autocapture: true,
        // One explicit pageview after consent is applied, even for old opt-out cookies.
        capture_pageview: false,
        capture_pageleave: true,
        capture_performance: false,
        // Starts opted out (nothing captured); syncPostHogConsent opts in every
        // allowed visitor with cookies (C11). No cookieless mode: this site is far
        // under the traffic threshold, and cookieless PostHog can't identify.
        persistence: 'localStorage+cookie',
        opt_out_capturing_by_default: true,
        // Replay only after an explicit Accept, started by syncPostHogConsent,
        // whatever the project's recording setting says.
        disable_session_recording: true,
        // Hard gate: nothing leaves the browser unless the shared decision allows it.
        before_send: function (event) {
          return window.__posthogMode === 'full' ? event : null;
        },
        loaded: function (sdk) {
          window.__posthog = window.__posthog || {};
          window.__posthog.loaded = true;
          (${syncPostHogConsent.toString()})(sdk);
        }
      });
    })();
  `;
}
